import React, { useEffect, useState, useMemo } from "react";
import { getMentoredUsers } from "../../../services/userService";
import { getMentorDashboard } from "../../../services/dashboardService";
import { getAllSyllabus } from "../../../services/syllabusService";
import { getUser } from "../../../utils/auth";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Users,
  Trophy,
  MessageSquare,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";

const SESSIONS = [
  {
    id: "S010424_310325",
    startTimestamp: 1711929600000,
    endTimestamp: 1743379200000,
  },
  {
    id: "S010424_310326",
    startTimestamp: 1711929600000,
    endTimestamp: 1774915200000,
  },
  {
    id: "S010425_310326",
    startTimestamp: 1743465600000,
    endTimestamp: 1774915200000,
  },
  {
    id: "S010425_310327",
    startTimestamp: 1743465600000,
    endTimestamp: 1806451200000,
  },
  {
    id: "S010426_310327",
    startTimestamp: 1775001600000,
    endTimestamp: 1806451200000,
  },
  {
    id: "S010426_310328",
    startTimestamp: 1775001600000,
    endTimestamp: 1838073600000,
  },
];

const Landing = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [syllabi, setSyllabi] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [errorUsers, setErrorUsers] = useState(null);

  const [selectedSyllabusId, setSelectedSyllabusId] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");

  const mentor = useMemo(() => getUser() || {}, []);

  // 1. Fetch mentored users and all syllabi
  useEffect(() => {
    const fetchData = async () => {
      if (!mentor.id) {
        setLoadingUsers(false);
        return;
      }
      setLoadingUsers(true);
      setErrorUsers(null);
      try {
        const [usersRes, syllabiRes] = await Promise.all([
          getMentoredUsers(mentor.id),
          getAllSyllabus(),
        ]);

        if (usersRes.status !== "success") {
          throw new Error(usersRes.message || "Failed to fetch users");
        }
        if (syllabiRes.status !== "success") {
          throw new Error(syllabiRes.message || "Failed to fetch syllabi");
        }

        const userList = usersRes.data || [];
        const syllabusList = syllabiRes.data || [];

        setUsers(userList);
        setSyllabi(syllabusList);

        // Determine default syllabus: Match the first student's syllabus if present, otherwise first available
        let defaultSyllabusId = "";
        if (userList.length > 0) {
          const firstUserSyllabusId = userList.find(
            (u) => u.syllabusId,
          )?.syllabusId;
          if (
            firstUserSyllabusId &&
            syllabusList.some((s) => s.id === firstUserSyllabusId)
          ) {
            defaultSyllabusId = firstUserSyllabusId;
          }
        }
        if (!defaultSyllabusId && syllabusList.length > 0) {
          defaultSyllabusId = syllabusList[0].id;
        }

        setSelectedSyllabusId(defaultSyllabusId);
        setSelectedSessionId(SESSIONS[4].id);
      } catch (err) {
        setErrorUsers(err.message);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchData();
  }, [mentor.id]);

  // 2. Fetch dashboard data when selected syllabus or session changes
  useEffect(() => {
    const fetchDashboard = async () => {
      if (!selectedSyllabusId || !selectedSessionId || !mentor.id) return;
      setDashboardLoading(true);
      setDashboardError(null);
      try {
        const res = await getMentorDashboard(
          mentor.id,
          selectedSyllabusId,
          selectedSessionId,
        );
        if (res.status === "success") {
          setDashboardData(res.data);
        } else {
          throw new Error(res.message || "Failed to fetch dashboard data");
        }
      } catch (err) {
        setDashboardError(err.message);
      } finally {
        setDashboardLoading(false);
      }
    };

    fetchDashboard();
  }, [selectedSyllabusId, selectedSessionId, mentor.id]);

  // 3. Filter students for directory
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const nameMatch = u.name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());
      const emailMatch = u.email
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());
      return nameMatch || emailMatch;
    });
  }, [users, searchQuery]);

  const handleStudentClick = (student) => {
    navigate(
      `/expert-view/report/${student.id}/${student.syllabusId}/${selectedSessionId}`,
      {
        state: student,
      },
    );
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  if (loadingUsers) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-screen bg-primaryLighter p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-primary font-medium text-base sm:text-lg">
            Loading dashboard and student data...
          </p>
        </div>
      </div>
    );
  }

  if (errorUsers) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-screen bg-primaryLighter p-4">
        <div className="bg-white border-2 border-red-200 rounded-2xl p-6 text-center max-w-md w-full shadow-sm">
          <AlertTriangle className="text-red-500 mx-auto mb-4 h-12 w-12" />
          <h3 className="text-lg font-bold text-gray-800 mb-2">
            Failed to load data
          </h3>
          <p className="text-sm text-gray-600 mb-4">{errorUsers}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full sm:w-auto px-6 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primaryDark transition shadow-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const overview = dashboardData?.overview || {};
  const feedbackSummary = dashboardData?.feedbackSummary || {};
  const topPerformers = dashboardData?.topPerformers || [];
  const weakStudents = dashboardData?.weakStudents || [];

  return (
    <div className="flex-1 flex flex-col lg:flex-row gap-4 p-4 lg:p-6 bg-primaryLighter min-h-screen overflow-x-hidden">
      {/* Left Column: Dashboard Stats and Analytics */}
      <div className="w-full flex flex-col gap-4 lg:max-w-[calc(100%-19rem)]">
        {/* Welcome Header and Filter Card Combined */}
        <div className="flex flex-col gap-4 rounded-xl border border-primaryLight bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          {/* Header Title Section */}
          <div>
            <h1 className="flex flex-wrap items-baseline gap-x-2 text-lg sm:text-xl font-bold text-primary">
              Dashboard Overview
              <span className="text-xs font-normal text-textSecondary">
                ({mentor.name || "Mentor"})
              </span>
            </h1>
            <p className="mt-1 text-xs text-textSecondary">
              Summary of students' performance and feedback actions.
            </p>
          </div>

          {/* Inline Filters Container */}
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 sm:justify-end">
            {/* Syllabus Selector */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2">
              <label
                htmlFor="syllabus-select"
                className="text-[10px] font-bold uppercase tracking-wider text-textSubHeading"
              >
                Syllabus:
              </label>
              <div className="relative w-full sm:w-auto">
                <select
                  id="syllabus-select"
                  value={selectedSyllabusId}
                  onChange={(e) => setSelectedSyllabusId(e.target.value)}
                  className="w-full sm:w-36 lg:max-w-[160px] cursor-pointer appearance-none rounded-lg border border-primaryLight bg-white py-2 sm:py-1.5 pl-3 pr-8 text-xs font-semibold text-primary transition focus:border-transparent focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {syllabi.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-primary">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Session Selector */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2">
              <label
                htmlFor="session-select"
                className="text-[10px] font-bold uppercase tracking-wider text-textSubHeading"
              >
                Session:
              </label>
              <div className="relative w-full sm:w-auto">
                <select
                  id="session-select"
                  value={selectedSessionId}
                  onChange={(e) => setSelectedSessionId(e.target.value)}
                  className="w-full sm:w-40 lg:max-w-[180px] cursor-pointer appearance-none rounded-lg border border-primaryLight bg-white py-2 sm:py-1.5 pl-3 pr-8 text-xs font-semibold text-primary transition focus:border-transparent focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {SESSIONS.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.id}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-primary">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard loading or error within the card section */}
        {dashboardLoading ? (
          <div className="bg-white rounded-xl border border-primaryLight p-8 text-center flex items-center justify-center min-h-[180px] shadow-sm">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary mb-2"></div>
              <p className="text-textSecondary text-xs">Fetching metrics...</p>
            </div>
          </div>
        ) : dashboardError ? (
          <div className="bg-red-50 rounded-xl border border-red-100 p-5 text-center shadow-sm">
            <AlertTriangle className="text-red-500 mx-auto mb-2 h-5 w-5" />
            <p className="text-red-700 font-medium text-xs m-0">
              {dashboardError}
            </p>
          </div>
        ) : !selectedSyllabusId || !selectedSessionId ? (
          <div className="bg-white rounded-xl border border-primaryLight p-8 text-center text-textSecondary text-xs shadow-sm">
            Please select a Syllabus and Session to view dashboard metrics.
          </div>
        ) : (
          <>
            {/* Metric Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Total Students Card */}
              <div className="bg-white p-4 rounded-xl border border-primaryLight flex items-center justify-between hover:shadow-md transition duration-300 shadow-sm">
                <div>
                  <p className="text-[10px] font-bold text-textSubHeading uppercase tracking-wider m-0">
                    Total Students
                  </p>
                  <h3 className="text-xl sm:text-2xl font-bold text-primary m-0 mt-1">
                    {overview.totalStudents ?? users.length}
                  </h3>
                </div>
                <div className="bg-blue-50/70 p-2.5 rounded-lg">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
              </div>

              {/* Average Score Card */}
              <div className="bg-white p-4 rounded-xl border border-primaryLight flex flex-col justify-between hover:shadow-md transition duration-300 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-bold text-textSubHeading uppercase tracking-wider m-0">
                      Average Score
                    </p>
                    <h3 className="text-xl sm:text-2xl font-bold text-primary m-0 mt-1">
                      {overview.averageScore !== undefined
                        ? `${overview.averageScore}%`
                        : "--"}
                    </h3>
                  </div>
                  <div className="bg-green-50/70 p-2.5 rounded-lg">
                    <Trophy className="h-5 w-5 text-green-600" />
                  </div>
                </div>
                {overview.averageScore !== undefined && (
                  <div className="mt-3 w-full">
                    <div className="w-full bg-gray-100 rounded-full h-1 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          overview.averageScore >= 75
                            ? "bg-success"
                            : overview.averageScore >= 45
                              ? "bg-warning"
                              : "bg-error"
                        }`}
                        style={{ width: `${overview.averageScore}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Open Feedback Card */}
              <div className="bg-white p-4 rounded-xl border border-primaryLight flex items-center justify-between hover:shadow-md transition duration-300 shadow-sm">
                <div>
                  <p className="text-[10px] font-bold text-textSubHeading uppercase tracking-wider m-0">
                    Pending Feedbacks
                  </p>
                  <h3 className="text-xl sm:text-2xl font-bold text-primary m-0 mt-1">
                    {feedbackSummary.openFeedbackCount ?? 0}
                  </h3>
                </div>
                <div className="bg-orange-50/70 p-2.5 rounded-lg">
                  <MessageSquare className="h-5 w-5 text-orange-600" />
                </div>
              </div>
            </div>

            {/* Performers & Weak Students Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Top Performers Card */}
              <div className="bg-white rounded-xl border border-primaryLight p-4 flex flex-col shadow-sm">
                <h3 className="text-sm font-bold text-successDark flex items-center gap-1.5 mb-3 border-b pb-2">
                  <Trophy className="h-4 w-4 text-success" />
                  Top Performers
                </h3>
                {topPerformers.length === 0 ? (
                  <p className="text-textSubHeading text-xs my-4 text-center">
                    No top performers data available.
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {topPerformers.map((performer, idx) => {
                      const student = users.find(
                        (u) => u.id === performer.userId,
                      );
                      return (
                        <div
                          key={idx}
                          onClick={() => student && handleStudentClick(student)}
                          className="flex items-center justify-between p-2 rounded-lg border border-gray-50 hover:border-successLight hover:bg-green-50/10 transition cursor-pointer gap-2"
                        >
                          <div className="flex items-center gap-2 overflow-hidden">
                            {student?.photoUrl ? (
                              <img
                                className="h-8 w-8 rounded-full object-cover border border-gray-200 flex-shrink-0"
                                src={student.photoUrl}
                                alt={student.name}
                              />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-green-100 text-green-700 font-bold flex items-center justify-center text-xs flex-shrink-0">
                                {getInitials(student?.name)}
                              </div>
                            )}
                            <div className="overflow-hidden">
                              <p className="m-0 text-xs font-semibold text-textPrimary truncate">
                                {student?.name || performer.userId}
                              </p>
                              <p className="m-0 text-[10px] text-textSubHeading truncate">
                                Strongest: {performer.strongestSubject || "N/A"}
                              </p>
                            </div>
                          </div>
                          <span className="bg-green-50 text-green-700 text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0">
                            {performer.score}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Weak Students Card */}
              <div className="bg-white rounded-xl border border-primaryLight p-4 flex flex-col shadow-sm">
                <h3 className="text-sm font-bold text-errorDark flex items-center gap-1.5 mb-3 border-b pb-2">
                  <AlertTriangle className="h-4 w-4 text-error" />
                  Needs Attention
                </h3>
                {weakStudents.length === 0 ? (
                  <p className="text-textSubHeading text-xs my-4 text-center">
                    No students need urgent attention.
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {weakStudents.map((weakUser, idx) => {
                      const student = users.find(
                        (u) => u.id === weakUser.userId,
                      );
                      return (
                        <div
                          key={idx}
                          onClick={() => student && handleStudentClick(student)}
                          className="flex items-center justify-between p-2 rounded-lg border border-gray-50 hover:border-errorLight hover:bg-red-50/10 transition cursor-pointer gap-2"
                        >
                          <div className="flex items-center gap-2 overflow-hidden">
                            {student?.photoUrl ? (
                              <img
                                className="h-8 w-8 rounded-full object-cover border border-gray-200 flex-shrink-0"
                                src={student.photoUrl}
                                alt={student.name}
                              />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-red-100 text-red-700 font-bold flex items-center justify-center text-xs flex-shrink-0">
                                {getInitials(student?.name)}
                              </div>
                            )}
                            <div className="overflow-hidden">
                              <p className="m-0 text-xs font-semibold text-textPrimary truncate">
                                {student?.name || weakUser.userId}
                              </p>
                              <p className="m-0 text-[10px] text-textSubHeading truncate">
                                Weakest: {weakUser.weakestSubject || "N/A"}
                              </p>
                            </div>
                          </div>
                          <span className="bg-red-50 text-red-700 text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0">
                            {weakUser.score}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Right Column: Compact Student Directory */}
      <div className="w-full lg:w-72 bg-white rounded-xl border border-primaryLight p-4 flex flex-col gap-3 lg:max-h-[calc(100vh-2rem)] lg:sticky lg:top-4 shadow-sm">
        <div>
          <h2 className="text-base font-bold text-primary m-0 flex items-center gap-1.5">
            <Users className="h-4.5 w-4.5 text-primary" />
            Students Directory
            <span className="text-xs bg-primaryLight text-primary px-2 py-0.5 rounded-full font-semibold">
              {users.length}
            </span>
          </h2>
          <p className="text-[10px] text-textSubHeading m-0 mt-0.5">
            Click to view student report
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search students..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-2 sm:py-1.5 border border-primaryLight rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent transition bg-gray-50/50"
          />
        </div>

        {/* Scrollable list */}
        <div className="flex-1 overflow-y-auto max-h-[350px] lg:max-h-none flex flex-col gap-1.5 pr-0.5">
          {filteredUsers.length === 0 ? (
            <p className="text-textSubHeading text-xs my-4 text-center">
              No students found.
            </p>
          ) : (
            filteredUsers.map((user) => (
              <div
                key={user.id}
                onClick={() => handleStudentClick(user)}
                className="group flex items-center justify-between p-2 rounded-lg hover:bg-primaryLighter border border-transparent hover:border-primaryLight transition duration-150 cursor-pointer gap-2"
              >
                <div className="flex items-center gap-2.5 overflow-hidden">
                  {user.photoUrl ? (
                    <img
                      className="h-8 w-8 rounded-full object-cover border border-gray-200 flex-shrink-0"
                      src={user.photoUrl}
                      alt={user.name}
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-primaryLight text-primary font-bold flex items-center justify-center text-xs flex-shrink-0">
                      {getInitials(user.name)}
                    </div>
                  )}

                  <div className="overflow-hidden">
                    <div className="flex items-center gap-1">
                      <h4 className="text-xs font-semibold text-textPrimary truncate m-0">
                        {user.name}
                      </h4>
                      <span
                        className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                          user.status === "active"
                            ? "bg-success"
                            : "bg-gray-400"
                        }`}
                        title={user.status}
                      />
                    </div>
                    <p className="text-[10px] text-textSubHeading truncate m-0 mt-0.5">
                      {user.email}
                    </p>
                  </div>
                </div>

                <ChevronRight className="h-3.5 w-3.5 text-gray-400 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-150 flex-shrink-0" />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Landing;
